import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabaseClient';
import LanguageSelector from '../components/LanguageSelector';
import Logo from '../components/Logo';
import { Lock, CheckCircle, AlertCircle } from 'lucide-react';
import { toast } from 'react-toastify';

const ResetPassword: React.FC = () => {
  const { t } = useTranslation();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [success, setSuccess] = useState(false);
  const { updatePassword } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const verifyRecoveryToken = async () => {
      try {
        setVerifying(true);
        
        // Get the hash fragment from the URL
        const hash = window.location.hash;
        
        // Get the token and type from the URL query parameters
        const params = new URLSearchParams(window.location.search);
        const token = params.get('token');
        const type = params.get('type');
        
        // Check if we have a hash (Supabase auth redirect) or token parameter
        if (!hash && (!token || type !== 'recovery')) {
          throw new Error(t('invalidResetLink'));
        }

        // If we have a hash, Supabase will handle the verification automatically
        // If we have a token, we need to verify it manually
        if (token && type === 'recovery') {
          const { error: verifyError } = await supabase.auth.verifyOtp({
            token,
            type: 'recovery'
          });

          if (verifyError) {
            throw verifyError;
          }
        }
        
        // Check if we have a session after verification
        const { data: { session } } = await supabase.auth.getSession();
        
        if (!session) {
          throw new Error(t('invalidResetLink'));
        }
        
        // If verification successful, we're ready to let the user set a new password
      } catch (error: any) {
        console.error('Error verifying recovery token:', error);
        setError(t('invalidResetLink'));
        
        // Redirect to forgot password after a delay
        setTimeout(() => {
          navigate('/forgot-password');
        }, 3000);
      } finally {
        setVerifying(false);
      }
    };

    verifyRecoveryToken();
  }, [t, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!password || !confirmPassword) {
      setError(t('pleaseAllFields'));
      return;
    }
    
    if (password !== confirmPassword) {
      setError(t('passwordsDoNotMatch'));
      return;
    }
    
    if (password.length < 6) {
      setError(t('passwordMinLength'));
      return;
    }
    
    try {
      setError(null);
      setLoading(true);
      
      const { error } = await updatePassword(password);
      if (error) throw error;
      
      setSuccess(true);
      toast.success(t('passwordUpdated'));
      
      // Clear form
      setPassword('');
      setConfirmPassword('');

      // Redirect to login after a delay
      setTimeout(() => {
        navigate('/login', { replace: true });
      }, 3000);
    } catch (error: any) {
      setError(error.message || t('passwordUpdateFailed'));
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100 p-4">
        <div className="absolute top-4 right-4">
          <LanguageSelector />
        </div>
        
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-8 text-center">
          <Logo size="large" className="mx-auto mb-6" />
          <h1 className="text-2xl font-bold text-gray-900 mb-4">{t('verifyingLink')}</h1>
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">{t('pleaseWait')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100 p-4">
        <div className="absolute top-4 right-4">
          <LanguageSelector />
        </div>
        
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-8 text-center">
          <Logo size="large" className="mx-auto mb-6" />
          <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-4">{t('invalidResetLink')}</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/forgot-password')}
            className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
          >
            {t('backToForgotPassword')}
          </button>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100 p-4">
        <div className="absolute top-4 right-4">
          <LanguageSelector />
        </div>
        
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-8 text-center">
          <Logo size="large" className="mx-auto mb-6" />
          <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-4">{t('passwordUpdated')}</h1>
          <p className="text-gray-600 mb-6">{t('passwordResetComplete')}</p>
          <button
            onClick={() => navigate('/login')}
            className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
          >
            {t('proceedToLogin')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 p-4">
      <div className="absolute top-4 right-4">
        <LanguageSelector />
      </div>
      
      <div className="max-w-md w-full bg-white rounded-lg shadow-md p-8">
        <div className="text-center mb-8">
          <Logo size="large" className="mx-auto mb-6" />
          <div className="flex justify-center mb-4">
            <Lock className="h-12 w-12 text-blue-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">{t('setNewPassword')}</h1>
          <p className="text-gray-600 mt-2">{t('createNewPassword')}</p>
        </div>
        
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}
        
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label htmlFor="password" className="block text-gray-700 text-sm font-medium mb-2">
              {t('newPassword')}
            </label>
            <input
              id="password"
              type="password"
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="••••••••"
              required
              autoComplete="new-password"
            />
          </div>
          
          <div className="mb-6">
            <label htmlFor="confirmPassword" className="block text-gray-700 text-sm font-medium mb-2">
              {t('confirmNewPassword')}
            </label>
            <input
              id="confirmPassword"
              type="password"
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="••••••••"
              required
              autoComplete="new-password"
            />
          </div>
          
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50"
          >
            {loading ? t('updating') : t('updatePassword')}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ResetPassword;